import React, { FC, useEffect, useRef, useState } from "react";
import { Select } from "antd";
import { getDictDataListSimpleApi } from "../../service/dict";
import "./VoiceOptions.scss";

type VoiceOptionsBase = {
    /** 下拉挂载节点，用于弹窗内时传 (trigger) => trigger.closest(".auto-model-content") || document.body */
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    /** 下拉层 z-index，弹窗内可传 1100 确保在 modal 之上 */
    dropdownZIndex?: number;
};

export type VoiceOptionsProps = VoiceOptionsBase &
    (
        | {
              filterMode?: "single";
              langCode: string;
              gender: string;
              age: string;
              timbre: string;
              onLangChange: (value: string) => void;
              onGenderChange: (value: string) => void;
              onAgeChange: (value: string) => void;
              onTimbreChange: (value: string) => void;
          }
        | {
              filterMode: "multi";
              langValues: string[];
              genderValues: string[];
              ageValues: string[];
              timbreValues: string[];
              onLangValuesChange: (value: string[]) => void;
              onGenderValuesChange: (value: string[]) => void;
              onAgeValuesChange: (value: string[]) => void;
              onTimbreValuesChange: (value: string[]) => void;
          }
    );

type FilterKey = "lang" | "gender" | "age" | "timbre";

const ChevronDown: FC = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

interface MultiMenuFieldProps {
    fieldKey: FilterKey;
    label: string;
    options: Dict.DictItem[];
    selectedValues: string[];
    onSelectedChange: (next: string[]) => void;
    isOpen: boolean;
    onToggle: () => void;
}

const MultiMenuField: FC<MultiMenuFieldProps> = ({
    fieldKey,
    label,
    options,
    selectedValues,
    onSelectedChange,
    isOpen,
    onToggle,
}) => {
    const summary =
        selectedValues.length === 0
            ? "全部"
            : selectedValues.length === 1
              ? options.find((o) => o.value === selectedValues[0])?.name ?? selectedValues[0]
              : `已选 ${selectedValues.length} 项`;

    const toggleValue = (value: string) => {
        if (selectedValues.includes(value)) {
            onSelectedChange(selectedValues.filter((v) => v !== value));
        } else {
            onSelectedChange([...selectedValues, value]);
        }
    };

    return (
        <div className={`voice-filter-ms voice-filter-ms--${fieldKey}`}>
            <button
                type="button"
                className={["voice-filter-ms__trigger", isOpen ? "voice-filter-ms__trigger--open" : ""]
                    .filter(Boolean)
                    .join(" ")}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                onClick={(e) => {
                    e.stopPropagation();
                    onToggle();
                }}
            >
                <span className="voice-filter-ms__trigger-label">{label}</span>
                <span className="voice-filter-ms__trigger-value">{summary}</span>
                <ChevronDown />
            </button>
            <div
                className={["voice-filter-ms__menu", isOpen ? "voice-filter-ms__menu--open" : ""]
                    .filter(Boolean)
                    .join(" ")}
                role="group"
                onMouseDown={(e) => e.stopPropagation()}
            >
                {options.map((opt) => (
                    <label key={opt.id} className="voice-filter-ms__row">
                        <input
                            type="checkbox"
                            className="voice-filter-ms__checkbox"
                            checked={selectedValues.includes(opt.value)}
                            onChange={() => toggleValue(opt.value)}
                        />
                        <span className="voice-filter-ms__row-text">{opt.name}</span>
                    </label>
                ))}
            </div>
        </div>
    );
};

const VoiceOptionsMulti: FC<Extract<VoiceOptionsProps, { filterMode: "multi" }>> = ({
    langValues,
    genderValues,
    ageValues,
    timbreValues,
    onLangValuesChange,
    onGenderValuesChange,
    onAgeValuesChange,
    onTimbreValuesChange,
}) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [openKey, setOpenKey] = useState<FilterKey | null>(null);

    const [langOptions, setLangOptions] = useState<Dict.DictItem[]>([]);
    const [genderOptions, setGenderOptions] = useState<Dict.DictItem[]>([]);
    const [ageOptions, setAgeOptions] = useState<Dict.DictItem[]>([]);
    const [timbreOptions, setTimbreOptions] = useState<Dict.DictItem[]>([]);

    useEffect(() => {
        const fetchDicts = async () => {
            try {
                const [langRes, genderRes, ageRes, timbreRes] = await Promise.all([
                    getDictDataListSimpleApi("target_language"),
                    getDictDataListSimpleApi("system_user_sex"),
                    getDictDataListSimpleApi("age_category"),
                    getDictDataListSimpleApi("voice_type"),
                ]);

                if (langRes.code === 0 && Array.isArray(langRes.data)) {
                    setLangOptions(langRes.data);
                }
                if (genderRes.code === 0 && Array.isArray(genderRes.data)) {
                    setGenderOptions(genderRes.data);
                }
                if (ageRes.code === 0 && Array.isArray(ageRes.data)) {
                    setAgeOptions(ageRes.data);
                }
                if (timbreRes.code === 0 && Array.isArray(timbreRes.data)) {
                    setTimbreOptions(timbreRes.data);
                }
            } catch (error) {
                console.error("获取字典数据失败:", error);
            }
        };

        void fetchDicts();
    }, []);

    useEffect(() => {
        const onDocMouseDown = (ev: MouseEvent) => {
            const el = rootRef.current;
            if (!el || !openKey) return;
            const t = ev.target as Node;
            if (!el.contains(t)) setOpenKey(null);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [openKey]);

    useEffect(() => {
        const onKey = (ev: KeyboardEvent) => {
            if (ev.key === "Escape") setOpenKey(null);
        };
        if (openKey) {
            document.addEventListener("keydown", onKey);
            return () => document.removeEventListener("keydown", onKey);
        }
    }, [openKey]);

    const toggle = (key: FilterKey) => {
        setOpenKey((prev) => (prev === key ? null : key));
    };

    return (
        <div
            ref={rootRef}
            className="voice-public-filters voice-public-filters--multiselect voice-public-filter"
        >
            <MultiMenuField
                fieldKey="lang"
                label="语种"
                options={langOptions}
                selectedValues={langValues}
                onSelectedChange={onLangValuesChange}
                isOpen={openKey === "lang"}
                onToggle={() => toggle("lang")}
            />
            <MultiMenuField
                fieldKey="gender"
                label="性别"
                options={genderOptions}
                selectedValues={genderValues}
                onSelectedChange={onGenderValuesChange}
                isOpen={openKey === "gender"}
                onToggle={() => toggle("gender")}
            />
            <MultiMenuField
                fieldKey="age"
                label="年龄"
                options={ageOptions}
                selectedValues={ageValues}
                onSelectedChange={onAgeValuesChange}
                isOpen={openKey === "age"}
                onToggle={() => toggle("age")}
            />
            <MultiMenuField
                fieldKey="timbre"
                label="类别"
                options={timbreOptions}
                selectedValues={timbreValues}
                onSelectedChange={onTimbreValuesChange}
                isOpen={openKey === "timbre"}
                onToggle={() => toggle("timbre")}
            />
        </div>
    );
};

export const VoiceOptions: FC<VoiceOptionsProps> = (props) => {
    if (props.filterMode === "multi") {
        return <VoiceOptionsMulti {...props} />;
    }

    const {
        langCode,
        gender,
        age,
        timbre,
        onLangChange,
        onGenderChange,
        onAgeChange,
        onTimbreChange,
        getPopupContainer,
        dropdownZIndex,
    } = props;

    const popupContainer = getPopupContainer ?? (() => document.body);
    const baseDropdownStyle = (extra: React.CSSProperties) => ({
        ...extra,
        ...(dropdownZIndex != null && { zIndex: dropdownZIndex }),
    });
    const [langOptions, setLangOptions] = useState<Dict.DictItem[]>([]);
    const [genderOptions, setGenderOptions] = useState<Dict.DictItem[]>([]);
    const [ageOptions, setAgeOptions] = useState<Dict.DictItem[]>([]);
    const [timbreOptions, setTimbreOptions] = useState<Dict.DictItem[]>([]);

    useEffect(() => {
        const fetchDicts = async () => {
            try {
                const [langRes, genderRes, ageRes, timbreRes] = await Promise.all([
                    getDictDataListSimpleApi("target_language"),
                    getDictDataListSimpleApi("system_user_sex"),
                    getDictDataListSimpleApi("age_category"),
                    getDictDataListSimpleApi("voice_type"),
                ]);

                if (langRes.code === 0 && Array.isArray(langRes.data)) {
                    setLangOptions(langRes.data);
                }
                if (genderRes.code === 0 && Array.isArray(genderRes.data)) {
                    setGenderOptions(genderRes.data);
                }
                if (ageRes.code === 0 && Array.isArray(ageRes.data)) {
                    setAgeOptions(ageRes.data);
                }
                if (timbreRes.code === 0 && Array.isArray(timbreRes.data)) {
                    setTimbreOptions(timbreRes.data);
                }
            } catch (error) {
                console.error("获取字典数据失败:", error);
            }
        };

        fetchDicts();
    }, []);

    return (
        <div className="voice-public-filters">
            <div className="filter-group">
                <span className="filter-label">语种</span>
                <Select
                    className="home-series__filter-select"
                    value={langCode}
                    onChange={onLangChange}
                    style={{ minWidth: 100 }}
                    getPopupContainer={popupContainer}
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={baseDropdownStyle({ overflowY: "visible", minHeight: "auto" })}
                >
                    <Select.Option value="">全部</Select.Option>
                    {langOptions.map((opt) => (
                        <Select.Option key={opt.id} value={opt.value}>
                            {opt.name}
                        </Select.Option>
                    ))}
                </Select>
            </div>
            <div className="filter-group">
                <span className="filter-label">性别</span>
                <Select
                    className="home-series__filter-select"
                    value={gender}
                    onChange={onGenderChange}
                    style={{ minWidth: 100 }}
                    getPopupContainer={popupContainer}
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={baseDropdownStyle({ maxHeight: 400, overflowY: "visible" })}
                >
                    <Select.Option value="">全部</Select.Option>
                    {genderOptions.map((opt) => (
                        <Select.Option key={opt.id} value={opt.value}>
                            {opt.name}
                        </Select.Option>
                    ))}
                </Select>
            </div>
            <div className="filter-group">
                <span className="filter-label">年龄</span>
                <Select
                    className="home-series__filter-select"
                    value={age}
                    onChange={onAgeChange}
                    style={{ minWidth: 100 }}
                    getPopupContainer={popupContainer}
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={baseDropdownStyle({ maxHeight: 400, overflowY: "visible" })}
                >
                    <Select.Option value="">全部</Select.Option>
                    {ageOptions.map((opt) => (
                        <Select.Option key={opt.id} value={opt.value}>
                            {opt.name}
                        </Select.Option>
                    ))}
                </Select>
            </div>
            <div className="filter-group">
                <span className="filter-label">类别</span>
                <Select
                    className="home-series__filter-select"
                    value={timbre}
                    onChange={onTimbreChange}
                    style={{ minWidth: 100 }}
                    getPopupContainer={popupContainer}
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={baseDropdownStyle({ overflowY: "visible", minHeight: "auto" })}
                >
                    <Select.Option value="">全部</Select.Option>
                    {timbreOptions.map((opt) => (
                        <Select.Option key={opt.id} value={opt.value}>
                            {opt.name}
                        </Select.Option>
                    ))}
                </Select>
            </div>
        </div>
    );
};
